import { Bell, CheckCheck, ChevronRight, Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { HelpBubble } from "@/features/help/components/HelpBubble";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn, formatDateTimeFr } from "@/lib/utils";

import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadCount,
} from "../hooks";
import type { Notification } from "../types";

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const unread = useUnreadCount();
  const list = useNotifications(1);
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const unreadCount = unread.data?.count ?? 0;
  const notifications = list.data?.results ?? [];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={
            unreadCount > 0
              ? `Notifications, ${unreadCount} non lues`
              : "Notifications"
          }
          className="relative text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
          data-help="nav.bell"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="flex items-center gap-1 text-sm font-semibold text-muted-foreground">
            Notifications
            <HelpBubble topic="nav.bell" variant="icon" side="bottom" align="start" />
          </span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs"
              disabled={markAll.isPending}
              onClick={() => markAll.mutate()}
            >
              {markAll.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <CheckCheck className="h-3 w-3" />
              )}
              Tout marquer lu
            </Button>
          )}
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {list.isLoading && (
            <div className="flex items-center justify-center gap-2 px-3 py-8 text-sm text-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
            </div>
          )}
          {!list.isLoading && notifications.length === 0 && (
            <p className="px-3 py-8 text-center text-sm text-muted">
              Aucune notification.
            </p>
          )}
          {notifications.map((notif) => (
            <NotificationRow
              key={notif.id}
              notif={notif}
              onMarkRead={() => markRead.mutate(notif.id)}
              onNavigate={(to) => {
                setOpen(false);
                navigate(to);
              }}
            />
          ))}
        </div>

        <div className="border-t border-border">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate("/notifications");
            }}
            className="flex w-full items-center justify-center gap-1 px-3 py-2 text-xs font-medium text-primary transition-colors hover:bg-muted/10"
          >
            Voir toutes les notifications
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface RowProps {
  notif: Notification;
  onMarkRead: () => void;
  onNavigate: (to: string) => void;
}

function NotificationRow({ notif, onMarkRead, onNavigate }: RowProps) {
  const actionable = !!notif.link;
  return (
    <button
      type="button"
      onClick={() => {
        if (!notif.is_read) onMarkRead();
        if (notif.link) onNavigate(notif.link);
      }}
      className={cn(
        "flex w-full flex-col items-start gap-0.5 border-b border-border px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-muted/10",
        !notif.is_read && "bg-primary/5",
        actionable && "cursor-pointer",
      )}
    >
      <div className="flex w-full items-start gap-2">
        {!notif.is_read && (
          <span
            className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
            aria-label="non lue"
          />
        )}
        <span
          className={cn(
            "flex-1 text-sm",
            notif.is_read ? "text-muted-foreground" : "font-medium text-muted-foreground",
          )}
        >
          {notif.rendered_subject || notif.template_code}
        </span>
        {actionable && (
          <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted" aria-hidden />
        )}
      </div>
      {notif.rendered_body && (
        <span className="line-clamp-2 pl-3.5 text-xs text-muted">
          {notif.rendered_body}
        </span>
      )}
      <span className="pl-3.5 text-[11px] text-muted">
        {formatDateTimeFr(notif.created_at)}
      </span>
    </button>
  );
}
